import React, { useState } from 'react';
import { Card, Form, InputNumber, Button, Row, Col, Alert, Space, Statistic, Divider } from 'antd';
import { Database, TrendingUp, AlertTriangle, Settings } from 'lucide-react';

interface StorageBufferSettings {
  bufferPercentage: number;
  minimumBuffer: number;
  maximumBuffer: number;
  autoAdjust: boolean;
}

export const StorageBufferTab: React.FC = () => {
  const [form] = Form.useForm();
  const [settings, setSettings] = useState<StorageBufferSettings>({
    bufferPercentage: 10,
    minimumBuffer: 50,
    maximumBuffer: 500,
    autoAdjust: true,
  });

  const handleSaveSettings = (values: StorageBufferSettings) => {
    setSettings(values);
    // Here you would typically save to backend
    console.log('Saving storage buffer settings:', values);
  };

  // Mock data for demonstration
  const currentUtilization = 75; // 75% of storage used
  const totalCapacity = 10000; // cubic feet
  const usedSpace = 7500; // cubic feet
  const bufferSpace = totalCapacity * (settings.bufferPercentage / 100);
  const availableSpace = totalCapacity - usedSpace - bufferSpace;

  const getUtilizationColor = (utilization: number) => {
    if (utilization > 90) return 'text-red-600';
    if (utilization > 80) return 'text-orange-600';
    if (utilization > 70) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getUtilizationStatus = (utilization: number) => {
    if (utilization > 90) return 'Critical';
    if (utilization > 80) return 'Warning';
    if (utilization > 70) return 'Caution';
    return 'Good';
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-2">Storage Buffer Management</h3>
        <p className="text-gray-500">Manage storage buffer to ensure adequate capacity for operations</p>
      </div>

      <Alert
        message="Storage Buffer Information"
        description="Storage buffer helps ensure adequate space for incoming inventory and prevents over-capacity issues. This setting was moved from the Storage tab to centralize all service metrics."
        type="info"
        showIcon
        className="mb-6"
      />

      <Row gutter={24}>
        {/* Current Status */}
        <Col span={16}>
          <Card title="Current Storage Status" className="mb-6">
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="Total Capacity"
                  value={totalCapacity.toLocaleString()}
                  suffix="ft³"
                  prefix={<Database size={16} />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Used Space"
                  value={usedSpace.toLocaleString()}
                  suffix="ft³"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Buffer Space"
                  value={bufferSpace.toLocaleString()}
                  suffix="ft³"
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Available Space"
                  value={availableSpace.toLocaleString()}
                  suffix="ft³"
                  valueStyle={{ color: availableSpace < 0 ? '#ff4d4f' : '#52c41a' }}
                />
              </Col>
            </Row>

            <Divider />

            <div className="text-center">
              <div className={`text-4xl font-bold mb-2 ${getUtilizationColor(currentUtilization)}`}>
                {currentUtilization}%
              </div>
              <div className="text-lg text-gray-600">Storage Utilization</div>
              <div className={`text-sm mt-1 ${getUtilizationColor(currentUtilization)}`}>
                Status: {getUtilizationStatus(currentUtilization)}
              </div>
            </div>

            {/* Visual Progress Bar */}
            <div className="mt-6">
              <div className="bg-gray-200 rounded-full h-6 relative">
                <div
                  className="bg-blue-500 h-6 rounded-l-full transition-all duration-300"
                  style={{ width: `${(usedSpace / totalCapacity) * 100}%` }}
                />
                <div
                  className="bg-green-500 h-6 absolute top-0 transition-all duration-300"
                  style={{ 
                    left: `${(usedSpace / totalCapacity) * 100}%`,
                    width: `${(bufferSpace / totalCapacity) * 100}%`
                  }}
                />
                {availableSpace < 0 && (
                  <div className="absolute top-0 right-0 bg-red-500 h-6 rounded-r-full w-2" />
                )}
              </div>
              <div className="flex justify-between mt-2 text-sm text-gray-600">
                <span>Used: {usedSpace.toLocaleString()} ft³</span>
                <span>Buffer: {bufferSpace.toLocaleString()} ft³</span>
                <span>Total: {totalCapacity.toLocaleString()} ft³</span>
              </div>
            </div>

            {availableSpace < 0 && (
              <Alert
                message="Storage Over Capacity"
                description={`Storage is over capacity by ${Math.abs(availableSpace).toLocaleString()} ft³. Consider adjusting buffer settings or increasing capacity.`}
                type="error"
                showIcon
                icon={<AlertTriangle size={16} />}
                className="mt-4"
              />
            )}
          </Card>
        </Col>

        {/* Buffer Settings */}
        <Col span={8}>
          <Card title="Buffer Settings" extra={<Settings size={16} />}>
            <Form
              form={form}
              layout="vertical"
              initialValues={settings}
              onFinish={handleSaveSettings}
            >
              <Form.Item
                name="bufferPercentage"
                label="Buffer Percentage"
                rules={[
                  { required: true, message: 'Please enter buffer percentage' },
                  { type: 'number', min: 1, max: 50, message: 'Buffer must be between 1-50%' }
                ]}
              >
                <InputNumber
                  min={1}
                  max={50}
                  step={1}
                  className="w-full"
                  placeholder="10"
                  addonAfter="%"
                />
              </Form.Item>

              <Form.Item
                name="minimumBuffer"
                label="Minimum Buffer"
                rules={[
                  { required: true, message: 'Please enter minimum buffer' },
                  { type: 'number', min: 0, message: 'Minimum buffer cannot be negative' }
                ]}
              >
                <InputNumber
                  min={0}
                  step={10}
                  className="w-full"
                  placeholder="50"
                  addonAfter="ft³"
                />
              </Form.Item>

              <Form.Item
                name="maximumBuffer"
                label="Maximum Buffer"
                rules={[
                  { required: true, message: 'Please enter maximum buffer' },
                  { type: 'number', min: 0, message: 'Maximum buffer cannot be negative' }
                ]}
              >
                <InputNumber
                  min={0}
                  step={50}
                  className="w-full"
                  placeholder="500"
                  addonAfter="ft³"
                />
              </Form.Item>

              <Form.Item className="mb-0">
                <Space className="w-full justify-end">
                  <Button onClick={() => form.resetFields()}>
                    Reset
                  </Button>
                  <Button type="primary" htmlType="submit">
                    Save Settings
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>
        </Col>
      </Row>

      {/* Buffer Impact Analysis */}
      <Card title="Buffer Impact Analysis">
        <Row gutter={16}>
          <Col span={8}>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <TrendingUp size={32} className="text-blue-600 mx-auto mb-2" />
              <div className="text-lg font-semibold text-blue-900">
                {((bufferSpace / totalCapacity) * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-blue-600">of Total Capacity</div>
            </div>
          </Col>
          <Col span={8}>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <Database size={32} className="text-green-600 mx-auto mb-2" />
              <div className="text-lg font-semibold text-green-900">
                {Math.max(0, Math.floor(availableSpace / 100))}
              </div>
              <div className="text-sm text-green-600">Days Until Full*</div>
            </div>
          </Col>
          <Col span={8}>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <AlertTriangle size={32} className="text-orange-600 mx-auto mb-2" />
              <div className="text-lg font-semibold text-orange-900">
                {availableSpace < 1000 ? 'Low' : 'Good'}
              </div>
              <div className="text-sm text-orange-600">Buffer Status</div>
            </div>
          </Col>
        </Row>
        <div className="mt-4 text-xs text-gray-500">
          * Estimated based on average daily intake of 100 ft³
        </div>
      </Card>

      {/* Recommendations */}
      <Card title="Recommendations">
        <div className="space-y-3">
          {availableSpace < 0 && (
            <Alert
              message="Immediate Action Required"
              description="Storage is over capacity. Consider reducing buffer percentage temporarily or moving items to offsite storage."
              type="error"
              showIcon
            />
          )}
          
          {availableSpace > 0 && availableSpace < 1000 && (
            <Alert
              message="Low Available Space"
              description="Available space is running low. Monitor incoming inventory and consider adjusting operations."
              type="warning"
              showIcon
            />
          )}
          
          {currentUtilization > 85 && (
            <Alert
              message="High Utilization"
              description="Storage utilization is high. Consider planning for capacity expansion or optimizing current inventory."
              type="info"
              showIcon
            />
          )}
          
          {availableSpace > 2000 && currentUtilization < 60 && (
            <Alert
              message="Optimal Storage Level"
              description="Storage levels are optimal with good buffer space available for operations."
              type="success"
              showIcon
            />
          )}
        </div>
      </Card>
    </div>
  );
}; 